/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { BaseCartState } from '@msdyn365-commerce/global-state';
import * as React from 'react';
import { ICipChannelNotificationProps } from './cip-channel-notification.props.autogenerated';
import { COMMERCE_CUSTOMER_IDENTITY_NOT_FOUND } from './constants';

// Monkey-patch BaseCartState to add error handling for channel notification
const originalInitialize = BaseCartState.prototype.initialize;
BaseCartState.prototype.initialize = async function(this: BaseCartState): Promise<void> {
    try {
        await originalInitialize.call(this);
    } catch (error) {
        // Handle 403 errors to detect CommerceCustomerIdentityNotFound
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err: any = error;

        const status = err?.status || err?.statusCode || err?.response?.status;

        if (status === 403) {
            const body = err?.body || err?.data || err?.response?.body;

            // Check for error identifier in the response body
            // The error might be directly in the body or nested in an Exception field (as a JSON string)
            let textToInspect = typeof body === 'string' ? body : JSON.stringify(body || {});

            // If body has an Exception field, also check within that JSON string
            if (body?.Exception && typeof body.Exception === 'string') {
                textToInspect += body.Exception;
            }

            if (textToInspect.includes(COMMERCE_CUSTOMER_IDENTITY_NOT_FOUND)) {
                // Dispatch custom event to notify the UI
                globalThis.dispatchEvent(new CustomEvent('cip:channelNotification:error'));
            }
        }

        // Re-throw the error
        throw error;
    }
};

export interface ICipChannelNotificationViewProps extends ICipChannelNotificationProps<CipChannelNotification> {}

/**
 *
 * CipChannelNotification component
 * @extends {React.PureComponent<ICipChannelNotificationProps<ICipChannelNotificationData>>}
 */
class CipChannelNotification extends React.PureComponent<ICipChannelNotificationProps<CipChannelNotification>> {
    public render(): JSX.Element | null {
        return this.props.renderView(this.props);
    }
}

export default CipChannelNotification;
